import _, {assign} from 'lodash';
import {connect} from 'react-redux';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {findEditModeChildTagIdsToRemove} from '../../../../helpers/models/tagHelpers';
import {VisualWorkspaceModel, VisualWorkspaceTag} from '../../../../models/api/visualWorkspaceModel';
import {
  updateLastAutoRecommendationAction,
  updateLastEditModeHelpTipAction,
} from '../../../../store/data/preferences/preferencesActions';
import {getLastAutoRecommendation} from '../../../../store/data/preferences/preferencesSelectors';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {isOverFourWeeksAgo} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {getVisualWorkspaceRarelyViewedCount} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {ToggleTeamCustomViewHandler, ToggleTeamInboxHandler} from './context/types';
import {ToggleLensHandler, ToggleTagHandler, ToggleTeammateHandler} from './context/workspacesEditorContext';
import {WorkspacesEditorAutoRecHelpTip} from './workspacesEditorAutoRecHelpTip';

interface WorkspacesEditorAutoRecHelpTipContainerProps {
  onToggleTag: ToggleTagHandler;
  onToggleTeammate: ToggleTeammateHandler;
  onToggleTeamCustomView: ToggleTeamCustomViewHandler;
  onToggleTeamInbox: ToggleTeamInboxHandler;
  onToggleLens: ToggleLensHandler;
  draftWorkspace: VisualWorkspaceModel;
  onWorkspacesWidthChange: HandlerOf<number>;
  size: number;
}

const mapStateToProps = makeMapStateToProps<WorkspacesEditorAutoRecHelpTipContainerProps>()(() => ({
  tags: getTags,
  rarelyViewedItemCount: getVisualWorkspaceRarelyViewedCount,
  lastAutoRecommendation: getLastAutoRecommendation,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  updateLastEditModeHelpTip: updateLastEditModeHelpTipAction,
  updateLastAutoRecommendation: updateLastAutoRecommendationAction,
}));

export const WorkspacesEditorAutoRecHelpTipContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {tags, rarelyViewedItemCount, lastAutoRecommendation} = stateProps;
    const {
      draftWorkspace,
      onToggleTag,
      onToggleTeammate,
      onToggleTeamInbox,
      onToggleTeamCustomView,
      onToggleLens,
      onWorkspacesWidthChange,
      size,
    } = ownProps;
    const {updateLastEditModeHelpTip, updateLastAutoRecommendation} = dispatchProps;

    const onUncheckAllTags = () => {
      const rarelyViewedTags = draftWorkspace.teammateWorkspaceTags.filter(
        ({isRecentlyViewed}) => isRecentlyViewed === false,
      );
      const selectedRarelyViewedTags = rarelyViewedTags.filter(({isSelected}) => isSelected);

      // Uncheck all selected rarely viewed tags and their children that are not rarely viewed.
      // Their children that are rarely viewed are unchecked separately.
      selectedRarelyViewedTags.map((teammateTag) => uncheckRarelyViewedTag(teammateTag.id, rarelyViewedTags));
    };

    const uncheckRarelyViewedTag = (tagId: number, rarelyViewedTags: VisualWorkspaceTag[]) => {
      const tagObject = tags[tagId];

      if (!tagObject) {
        return;
      }

      const combinedTags = assign(tags);
      const visibleTagIds = draftWorkspace.teammateWorkspaceTags.map((tag) => tag.id);
      const allChildTagIds = findEditModeChildTagIdsToRemove({
        tagId,
        visibleTagIds,
        parentTagOverridesMap: {},
        combinedTags,
      });

      const allChildTags = _(allChildTagIds)
        // If the child tag is also rarely viewed, filter it out so we don't toggle it twice.
        .filter((id) => !rarelyViewedTags.some((rarelyViewedTag) => rarelyViewedTag.id === id))
        .map((id) => combinedTags[id])
        .compact()
        .value();

      onToggleTag({tag: tagObject, allParentTags: [], allChildTags, ifUsedHelpTip: true});
    };

    const onUncheckAllClick = () => {
      draftWorkspace.teammates.items
        .filter(({isRecentlyViewed, isSelected}) => isRecentlyViewed === false && isSelected)
        .forEach((teammate) => onToggleTeammate(teammate.id, true));

      draftWorkspace.lenses.items
        .filter(({isRecentlyViewed, isSelected}) => isRecentlyViewed === false && isSelected)
        .forEach((lens) => onToggleLens(lens.id, true));

      draftWorkspace.teams.forEach((teamData) => {
        teamData.customViews
          .filter(({isRecentlyViewed, isSelected}) => isRecentlyViewed === false && isSelected)
          .forEach((customView) =>
            onToggleTeamCustomView({id: customView.id, teamId: teamData.teamId}, true),
          );
        teamData.inboxes
          .filter(({isRecentlyViewed, isSelected}) => isRecentlyViewed === false && isSelected)
          .forEach((teamInbox) => onToggleTeamInbox({id: teamInbox.id, teamId: teamData.teamId}, true));
      });

      onUncheckAllTags();
    };

    const onCloseHelpTip = () => {
      if (!isOverFourWeeksAgo(lastAutoRecommendation) && lastAutoRecommendation) {
        updateLastEditModeHelpTip(lastAutoRecommendation);
      } else {
        const currentTime = Date.now();
        updateLastEditModeHelpTip(currentTime);
        updateLastAutoRecommendation(currentTime);
      }
      // Update workspace width so there isn't a jump when we close the help tip.
      onWorkspacesWidthChange(size);
    };

    return {
      ...ownProps,
      onUncheckAllClick,
      rarelyViewedItemCount,
      onCloseHelpTip,
    };
  },
)(WorkspacesEditorAutoRecHelpTip);
